/**
 * Firebase error handling utilities
 * Provides user-friendly error messages for Firebase/Firestore operations
 */

interface ErrorWithCode {
  code?: string;
  message?: string;
}

/**
 * Check if an error is a Firebase permission denied error
 */
export function isPermissionDeniedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const errorObj = error as ErrorWithCode;
  return errorObj.code === "permission-denied";
}

/**
 * Get a user-friendly error message for Firebase errors
 *
 * @param error - The error object from Firebase
 * @param operation - Optional context about what operation was being attempted (e.g., "create trial")
 * @returns User-friendly error message
 */
export function getFirebaseErrorMessage(
  error: unknown,
  operation?: string
): string {
  if (!error || typeof error !== "object") {
    return operation
      ? `Failed to ${operation}. Please try again.`
      : "An error occurred. Please try again.";
  }

  const errorObj = error as ErrorWithCode;

  // Handle permission denied errors
  if (isPermissionDeniedError(error)) {
    return operation
      ? `Could not ${operation}. You don't have the necessary permissions.`
      : "You don't have the necessary permissions to perform this action.";
  }

  // Handle other common Firebase error codes
  switch (errorObj.code) {
    case "unavailable":
      return "The service is temporarily unavailable. Please try again later.";
    case "cancelled":
      return "The operation was cancelled.";
    case "deadline-exceeded":
      return "The operation timed out. Please try again.";
    case "resource-exhausted":
      return "Too many requests. Please wait a moment and try again.";
    case "not-found":
      return operation
        ? `Could not ${operation}. The item was not found.`
        : "The requested item was not found.";
    case "already-exists":
      return operation
        ? `Could not ${operation}. An item with this identifier already exists.`
        : "An item with this identifier already exists.";
    case "failed-precondition":
      return "The operation failed due to a conflict. Please refresh and try again.";
    case "aborted":
      return "The operation was aborted. Please try again.";
    case "internal":
      return "An internal error occurred. Please try again later.";
    case "unauthenticated":
      return "Your session has expired. Please sign in again.";
    case "storage/unauthorized":
      return operation
        ? `Could not ${operation}. You don't have permission to access this file.`
        : "You don't have permission to access this file.";
    default:
      break;
  }

  // Fallback: use the original error message if available
  if (errorObj.message) {
    return operation
      ? `Failed to ${operation}: ${errorObj.message}`
      : errorObj.message;
  }

  return operation
    ? `Failed to ${operation}. Please try again.`
    : "An error occurred. Please try again.";
}

/** Thrown locally when a capture has no entry to attribute the save to. */
export const MISSING_ENTRY_PATH_ERROR = "Missing entry path for annotation save";

const GENERIC_ANNOTATION_SAVE_FAILURE = "Failed to save annotations. Please try again.";

/**
 * Describe why an annotation save failed, in terms a labeler can act on.
 *
 * The distinction that matters is retryable vs not. A rejected payload or a
 * permission problem will fail identically forever, so telling the user to
 * "try again" sends them into a loop that cannot end — which is exactly how a
 * single out-of-bounds seeded box turned into an unsaveable image. Those cases
 * carry the server's own reason and ask for a report instead.
 */
export function getAnnotationSaveErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message === MISSING_ENTRY_PATH_ERROR) {
    return "Cannot save annotations because this image is not linked to an entry.";
  }
  if (!error || typeof error !== "object") return GENERIC_ANNOTATION_SAVE_FAILURE;

  const { code, message } = error as ErrorWithCode;
  // Callable rejections arrive as `functions/<status>`; bare Firestore errors don't.
  const status = typeof code === "string" ? code.replace(/^functions\//, "") : undefined;
  const detail = typeof message === "string" && message.trim() ? message.trim().replace(/\.$/, "") : undefined;

  switch (status) {
    case "invalid-argument":
      return detail
        ? `Server rejected the save: ${detail}. Retrying will not help — please report this with the plot and sample number.`
        : "Server rejected the save. Retrying will not help — please report this with the plot and sample number.";
    case "failed-precondition":
      return detail
        ? `This image cannot be saved in its current state: ${detail}. Refresh the page and check the image.`
        : "This image cannot be saved in its current state. Refresh the page and check the image.";
    case "permission-denied":
      return detail
        ? `You don't have permission to save annotations on this image: ${detail}.`
        : "You don't have permission to save annotations on this image.";
    case "unauthenticated":
      return "Your session has expired. Sign in again, then redo this save.";
    case "not-found":
      return detail
        ? `Could not find this image's saved annotation data: ${detail}. Refresh the page before saving.`
        : "Could not find this image's saved annotation data. Refresh the page before saving.";
    case "unavailable":
      return "The server is temporarily unavailable. Please try again.";
    case "deadline-exceeded":
      return "The save timed out. Check your connection and please try again.";
    case "resource-exhausted":
      return "Too many requests. Wait a moment, then please try again.";
    default:
      return GENERIC_ANNOTATION_SAVE_FAILURE;
  }
}
